// Reviewing a generated phase, on the client.
//
// Pure functions only. The server decides what is committable and what is
// warned about; this decides how a coach reads it, and which line each warning
// belongs to.
//
// The rule running through it: **a warning is attached to a line, not floating
// above the page.** A list of twelve warnings above a forty-line programme is
// something a coach scrolls past. The same twelve shown against the lines they
// concern is a review.

export const DRAFT_STATUS = {
  PENDING: 'pending',
  READY: 'ready',
  FAILED: 'failed',
  COMMITTED: 'committed',
};

/** Whether a draft is still being generated, and should be polled. */
export function isPending(draft) {
  return draft?.status === DRAFT_STATUS.PENDING;
}

/**
 * What to tell the coach about where a draft is.
 *
 * @returns {{tone: 'working'|'ready'|'error'|'done', text: string}}
 */
export function statusLabel(draft) {
  switch (draft?.status) {
    case DRAFT_STATUS.PENDING:
      return pendingLabel(draft);
    case DRAFT_STATUS.READY:
      return { tone: 'ready', text: 'Ready to review' };
    case DRAFT_STATUS.FAILED:
      return { tone: 'error', text: draft.error || 'Generation failed' };
    case DRAFT_STATUS.COMMITTED:
      return { tone: 'done', text: 'Saved to the programme' };
    default:
      return { tone: 'working', text: 'Starting…' };
  }
}

// ---------------------------------------------------------------------------
// A failed draft: why, and what the coach can do about it.
//
// LTS_RAG fails a large share of generations, and every failure used to look
// the same — a red line of service output, and nothing to press. The server now
// classes each one (models.FailureKind): TRANSIENT is the service, and the same
// brief will probably work; VERDICT is the service refusing this brief, and
// sending it again gets the same answer. The two need different buttons. "Retry"
// on a verdict cannot work; "Edit brief" on a blip sends the coach to rewrite a
// brief that was fine.
// ---------------------------------------------------------------------------

/** The failure classes the server sends. */
export const FAILURE_KIND = {
  TRANSIENT: 'transient',
  VERDICT: 'verdict',
};

/** What the failed draft's button does. */
export const FAILURE_ACTION = {
  RETRY: 'retry',
  EDIT_BRIEF: 'edit-brief',
};

/**
 * What to tell the coach about a failed draft, and the one thing to offer.
 *
 * The reason is the server's, shown as it is: it is the only part that says
 * what actually happened. The headline and the hint say what it MEANS, which is
 * the part a coach could not work out from "returned 502".
 *
 * A draft with no class failed before failures were classed. Its brief is
 * stored, so it can be retried; the retry classes whatever happens next.
 *
 * @returns {null|{headline: string, reason: string, hint: string,
 *   action: string, actionLabel: string, reference?: string}}
 */
export function failureNotice(draft) {
  if (draft?.status !== DRAFT_STATUS.FAILED) return null;
  const reason = String(draft.error ?? '').trim() || 'No reason was recorded.';

  const notice = {};
  if (draft.failureKind === FAILURE_KIND.VERDICT) {
    notice.headline = 'The generator refused this brief.';
    notice.hint = 'Sending the same brief again would get the same answer. Change it and draft again.';
    notice.action = FAILURE_ACTION.EDIT_BRIEF;
    notice.actionLabel = 'Edit brief';
  } else {
    const attempts = Number(draft.attempt) || 1;
    const tried = attempts > 1 ? ` It has been tried ${attempts} times.` : '';
    if (draft.failureKind === FAILURE_KIND.TRANSIENT) {
      notice.headline = 'The generator had a problem that usually passes.';
      notice.hint = `Nothing is wrong with the brief, so retrying it as it is should work.${tried}`;
    } else {
      notice.headline = 'Generation failed.';
      notice.hint = `Retry it as it is, or start a new draft with a different brief.${tried}`;
    }
    notice.action = FAILURE_ACTION.RETRY;
    notice.actionLabel = 'Retry';
  }

  notice.reason = reason;

  // Add reference if engine request ID is available
  if (draft.engineRequestId) {
    notice.reference = draft.engineRequestId;
  }

  return notice;
}

/**
 * Format the reference text for displaying the engine request ID.
 *
 * @returns {string|null}
 */
export function referenceText(draft) {
  if (!draft?.engineRequestId) return null;
  return `Reference: ${draft.engineRequestId}`;
}

/**
 * The brief form's state, reopened from a draft's stored brief.
 *
 * "Edit brief" starts from what was asked for, not from blank: a refusal is
 * usually about one field, and retyping the other eight to change it is how a
 * coach ends up sending a different brief than the one they meant to fix. The
 * policy and position travel too — they are part of what was sent.
 */
export function briefFromDraft(draft) {
  const base = emptyBrief();
  const stored = draft?.brief;
  if (!stored || typeof stored !== 'object') return base;

  const brief = { ...base };
  for (const key of Object.keys(base)) {
    if (stored[key] !== undefined && stored[key] !== null) brief[key] = stored[key];
  }
  if (stored.sequenceSource) brief.sequenceSource = stored.sequenceSource;
  if (stored.sqPosition) brief.sqPosition = stored.sqPosition;
  return brief;
}

/** Where a pending draft's generation is, as the server reports it. */
export const DRAFT_STAGE = {
  QUEUED: 'queued',
  RUNNING: 'running',
};

/**
 * A pending draft, told apart by stage.
 *
 * The generation service runs one job at a time, so a draft can sit in a queue
 * for several of someone else's generations before its own starts. "Writing the
 * phase…" for all of that reads as a hang — on a phone, the plausible reading
 * is that the network dropped it. Saying it is queued, and how far back, is
 * what lets a coach wait instead of resubmitting into the same queue.
 *
 * queuePosition counts the jobs that start first, including the running one.
 * Absent is not zero: zero means it starts next, absent means the server did
 * not say, and that is rendered without a number rather than as "0 ahead".
 */
function pendingLabel(draft) {
  if (draft.stage !== DRAFT_STAGE.QUEUED) {
    return { tone: 'working', text: 'Writing the phase…' };
  }
  const ahead = draft.queuePosition;
  if (typeof ahead !== 'number') {
    return { tone: 'working', text: 'Queued for the generator…' };
  }
  if (ahead === 0) {
    return { tone: 'working', text: 'Queued — starts next…' };
  }
  return {
    tone: 'working',
    text: `Queued — ${ahead} generation${ahead === 1 ? '' : 's'} ahead…`,
  };
}

/**
 * How long a pending draft's hint should say it takes. A queued draft waits for
 * the jobs ahead of it too, so "a minute or two" would be a promise the queue
 * breaks.
 */
export function pendingHint(draft) {
  if (draft?.stage === DRAFT_STAGE.QUEUED) {
    return 'It starts when the generations ahead of it finish; you can leave this page.';
  }
  return 'This usually takes a minute or two.';
}

/**
 * Consecutive failed polls tolerated before the coach is told.
 *
 * One failed poll says nothing about the draft — the server keeps generating
 * whatever happens to one status request — and on a phone a request fails
 * whenever the signal does. Giving up on the first one stopped polling for
 * good, leaving a draft that finished upstream showing as pending until a
 * reload.
 */
export const POLL_FAILURES_BEFORE_NOTICE = 3;

/**
 * What a failed poll means, and whether to keep polling.
 *
 * Only a failure that could pass is retried: no response at all (the network),
 * or a 5xx/429 (the server, briefly). A 401 is an expired session and a 404 a
 * draft that is gone; polling those every thirty seconds forever, under a
 * notice saying "still trying", hides the one thing the coach can do about it.
 *
 * @returns {{retry: boolean, notice: {tone: string, message: string}|null}}
 */
export function pollFailure(error, failures) {
  const status = error?.response?.status;
  if (status === 401) {
    return {
      retry: false,
      notice: { tone: 'error', message: 'Your session has expired. Sign in again to see the draft.' },
    };
  }
  if (status === 404) {
    return {
      retry: false,
      notice: { tone: 'error', message: 'That draft no longer exists.' },
    };
  }
  if (status && status < 500 && status !== 429) {
    return {
      retry: false,
      notice: { tone: 'error', message: 'Could not check on the draft. Reload to see where it got to.' },
    };
  }
  return {
    retry: true,
    notice: failures >= POLL_FAILURES_BEFORE_NOTICE
      ? {
        tone: 'error',
        message: 'Can\u2019t reach the server to check on the draft. Still trying \u2014 it keeps generating meanwhile.',
      }
      : null,
  };
}

/** The delay before the next poll, backing off while polls keep failing. */
export function nextPollDelay(baseMs, failures) {
  const steps = Math.min(Math.max(failures, 0), 4);
  return Math.min(baseMs * 2 ** steps, 30000);
}

/** The warning kinds the server can send. */
export const WARNING_KIND = {
  UNRESOLVED: 'unresolved_exercise',
  INVALID: 'invalid_prescription',
  MALFORMED_SERIES: 'malformed_series',
  UNKNOWN_QUALITY: 'unknown_strength_quality',
};

/**
 * Whether a warning must be cleared before the phase can be saved.
 *
 * Only an unresolved exercise blocks. A long rest or an odd series is the
 * coach's call — the generated programme is a draft, not a proposal to be
 * rejected wholesale.
 */
export function isBlocking(warning) {
  return warning?.kind === WARNING_KIND.UNRESOLVED;
}

/**
 * Index a draft's warnings by the line they concern.
 *
 * Keyed "<session>::<series>", with warnings that name no line collected under
 * an empty key so nothing is silently dropped from the review.
 *
 * @returns {{byLine: Object, general: Array, blockingCount: number}}
 */
export function indexWarnings(warnings) {
  const byLine = {};
  const general = [];
  let blockingCount = 0;

  for (const warning of warnings ?? []) {
    if (isBlocking(warning)) blockingCount += 1;

    // Both parts are needed to identify a line. A warning missing either cannot
    // be placed, and is shown as a general one rather than guessed at or lost.
    if (warning.series && warning.session) {
      const key = lineKey(warning.session, warning.series);
      byLine[key] = byLine[key] ?? [];
      byLine[key].push(warning);
    } else {
      general.push(warning);
    }
  }

  return { byLine, general, blockingCount };
}

/** The key a warning and its line agree on. */
export function lineKey(sessionName, series) {
  return `${sessionName ?? ''}::${series ?? ''}`;
}

/**
 * The warnings for one line of one session.
 *
 * Matched on session AND series, never on series alone. Every training day has
 * an A1, so a series-only match puts "not in the library" against the squat that
 * resolved perfectly. The server names the session on every line-scoped warning
 * for exactly this reason (RAG-9b).
 */
export function warningsForLine(index, sessionName, series) {
  return index?.byLine?.[lineKey(sessionName, series)] ?? [];
}

/**
 * A one-line summary of what still stands between the coach and saving.
 *
 * Distinguishes "nothing to fix" from "nothing blocking, but look at these" —
 * conflating them either nags about a rest the coach chose, or hides an
 * exercise that does not exist.
 */
export function reviewSummary(draft) {
  const warnings = draft?.draft?.warnings ?? [];
  const { blockingCount } = indexWarnings(warnings);
  const other = warnings.length - blockingCount;

  if (blockingCount > 0) {
    return {
      committable: false,
      text: `${blockingCount} exercise${blockingCount === 1 ? '' : 's'} `
        + 'must be matched to the library before this can be saved.',
    };
  }
  if (other > 0) {
    return {
      committable: true,
      text: `${other} thing${other === 1 ? '' : 's'} to look at, none of which stops you saving.`,
    };
  }
  return { committable: true, text: 'Nothing to fix.' };
}

/**
 * How many prescriptions a draft holds, for a summary line.
 */
export function countLines(draft) {
  return (draft?.draft?.sessions ?? []).reduce(
    (total, session) => total + (session.lines?.length ?? 0),
    0
  );
}

/**
 * The request body for saving the coach's edits.
 *
 * Sends exerciseId, never a name: picking by id is what makes the choice
 * unambiguous, and re-sending a name would reintroduce the spelling problem the
 * server's resolver exists to handle.
 */
// toPercent reads an edited intensity back as a number the API will accept.
//
// Null rather than 0 for an empty box: they are different prescriptions. Zero
// would be stored and rendered as "@ 0% 1RM", which is not a thing anybody wrote,
// where null correctly means no percentage applies to this movement — the state
// every C-series corrective is in.
export function toPercent(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildDraftUpdate(draft) {
  return {
    phaseName: draft?.phaseName ?? '',
    phaseNumber: Number(draft?.phaseNumber) || 1,
    strengthQuality: draft?.strengthQuality ?? '',
    sessions: (draft?.sessions ?? []).map((session) => ({
      name: session.name ?? '',
      lines: (session.lines ?? []).map((line) => ({
        series: line.series ?? '',
        exerciseId: Number(line.exerciseId) || 0,
        sets: Number(line.sets) || 0,
        repScheme: line.repScheme ?? '',
        tempo: line.tempo ?? '',
        rest: Number(line.rest) || 0,
        // A PUT is a full replace, so anything omitted here is destroyed. Body
        // part was already being lost this way on every edit; percentOneRM and
        // notes would have gone the same way.
        percentOneRM: toPercent(line.percentOneRM),
        notes: line.notes ?? '',
        bodyPart: line.bodyPart ?? '',
      })),
    })),
  };
}

// ---------------------------------------------------------------------------
// Asking the pipeline what to write next.
//
// The service keeps one client per programme so its progression analysis has a
// history to read (RAG-19). This is the coach's side of that: a brief filled in
// from what the athlete has actually done, rather than from the coach's memory
// of a block they wrote weeks ago.
// ---------------------------------------------------------------------------

/**
 * The brief a fresh form starts with.
 *
 * Every field the API accepts is present from the start, including the ones the
 * form has no input for. A recommendation fills those in, and they have to have
 * somewhere to live or the parts of it the coach cannot see are silently lost
 * between arriving and being sent back.
 */
export function emptyBrief() {
  return {
    phase: '',
    sq: '',
    sessions: 3,
    notes: '',
    phaseNumber: 0,
    focus: '',
    trainingAge: '',
    volumeTier: '',
    equipment: '',
  };
}

/**
 * Fold a recommendation into the brief the coach is editing.
 *
 * The recommendation wins everywhere EXCEPT notes the coach has already typed.
 * Notes are the one field where the coach knows something the pipeline cannot:
 * an injury from Tuesday, a competition next month. Overwriting that with a
 * recommendation derived from last month's training discards the only input the
 * analysis had no access to.
 */
export function mergeRecommendation(current, recommended) {
  const base = { ...emptyBrief(), ...(current ?? {}) };
  if (!recommended) return base;

  const typedNotes = String(base.notes ?? '').trim();

  // The sequence fields describe THIS recommendation, so they are replaced, never
  // kept: a KILO cell left over from an earlier suggestion would travel with a
  // default one and tell the model to prescribe a cell nobody chose. Absent
  // means absent — the keys are dropped, not set to empty — so a default brief
  // is the same object it always was.
  const { sequenceSource: _source, sqPosition: _position, ...rest } = base;
  const sequence = {};
  if (recommended.sequenceSource) sequence.sequenceSource = recommended.sequenceSource;
  if (recommended.sqPosition) sequence.sqPosition = recommended.sqPosition;

  return {
    ...rest,
    ...sequence,
    phase: recommended.phase ?? base.phase,
    sq: recommended.sq ?? base.sq,
    sessions: recommended.sessions ?? base.sessions,
    phaseNumber: recommended.phaseNumber ?? base.phaseNumber,
    focus: recommended.focus ?? base.focus,
    trainingAge: recommended.trainingAge ?? base.trainingAge,
    volumeTier: recommended.volumeTier ?? base.volumeTier,
    equipment: recommended.equipment ?? base.equipment,
    notes: typedNotes === '' ? (recommended.notes ?? '') : base.notes,
  };
}

/**
 * The recommended fields the form has no input for.
 *
 * Shown read-only beside the form, because they are sent with the brief. A
 * field that travels to the model without appearing anywhere is a decision made
 * on the coach's behalf and attributed to them.
 *
 * @returns {Array<{label: string, value: string}>}
 */
export function carriedBriefFields(brief) {
  const carried = [
    { label: 'Block number', value: brief?.phaseNumber > 0 ? String(brief.phaseNumber) : '' },
    { label: 'Focus', value: brief?.focus ?? '' },
    { label: 'Training age', value: brief?.trainingAge ?? '' },
    { label: 'Volume', value: brief?.volumeTier ?? '' },
    { label: 'Equipment', value: brief?.equipment ?? '' },
  ];
  return carried.filter((field) => String(field.value).trim() !== '');
}

/**
 * The body for starting a draft.
 *
 * Carries every field of the brief, so a recommendation the coach accepted
 * actually reaches the model. Sending only what the form renders was the first
 * version, and it dropped focus, training age, volume tier and equipment — the
 * four fields the recommendation exists to supply.
 */
export function briefRequest(brief) {
  const state = { ...emptyBrief(), ...(brief ?? {}) };
  // The policy and position that recommended this brief go back with it, so
  // generation sees the provenance the recommendation had. Only when present:
  // a default brief is sent exactly as before.
  const sequence = {};
  if (state.sequenceSource) sequence.sequenceSource = state.sequenceSource;
  if (state.sqPosition) sequence.sqPosition = state.sqPosition;
  return {
    ...sequence,
    phase: state.phase,
    sq: state.sq,
    sessions: Number(state.sessions) || 3,
    notes: state.notes,
    phaseNumber: Number(state.phaseNumber) || 0,
    focus: state.focus,
    trainingAge: state.trainingAge,
    volumeTier: state.volumeTier,
    equipment: state.equipment,
  };
}

/**
 * What to say when a recommendation could not be given.
 *
 * A 409 is not a failure: it means this programme has never been drafted, so
 * there is no history to recommend from. Rendering it in the error tone tells a
 * coach something is broken on the first block of every new programme, which is
 * the one time the message is guaranteed to appear.
 */
export function recommendationNotice(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.error;

  // A sequence policy that cannot answer. Checked before the plain 409, which
  // is the no-history case: a refusal is the coach's chosen sequence saying
  // "not this", and reading it as "no history yet" would hide it.
  const refusal = sequenceRefusalMessage(error);
  if (refusal) return { tone: 'error', message: refusal };

  if (status === 409) {
    return {
      tone: 'info',
      message: message
        || 'No history to go on yet — write this first block yourself.',
    };
  }
  return {
    tone: 'error',
    message: message || 'Could not get a suggestion. Fill the brief in yourself.',
  };
}

/** The class a notice renders with. */
export function noticeClass(notice) {
  if (notice?.tone === 'error') return 'status-error';
  if (notice?.tone === 'info') return 'draft-summary muted';
  return 'status-success';
}

// ---------------------------------------------------------------------------
// Choosing the sequence policy (LTS_RAG docs/mvp/sequence-policy.md).
//
// The service can pick the next block four ways. The coach chooses which one
// answers "Suggest from history"; the self-coached screen never offers this and
// always gets the default.
// ---------------------------------------------------------------------------

/** The sequence selector's options. '' is the service's implicit default. */
export const SEQUENCE_OPTIONS = [
  { value: '', label: 'Default (legacy)' },
  { value: 'kilo', label: 'KILO long-term model' },
  { value: 'corpus', label: 'Corpus' },
  { value: 'coach', label: 'Coach plan' },
];

/**
 * The body for asking for a recommendation.
 *
 * The default sends `{}` — exactly what this screen always sent — rather than
 * naming `legacy`, because only the implicit default is byte-identical upstream.
 */
export function recommendRequest(sequence) {
  return sequence ? { sequenceSource: sequence } : {};
}

/**
 * The "Sequence policy: …" sentence the service puts first in its reasoning
 * when a policy was chosen, or '' when it gave none.
 */
export function sequenceReasoningLine(reasoning) {
  const text = String(reasoning ?? '').trim();
  if (!text.startsWith('Sequence policy:')) return '';
  // The line ends at the sentence that closes the parenthesis: "…source kilo:x)."
  const end = text.indexOf(').');
  return end === -1 ? text : text.slice(0, end + 2);
}

/**
 * Where in the strength quality's spectrum the block sits, in words.
 *
 * Built from the validated fields only, the same way the service renders it in
 * the prompt, so the coach reads what the model will read.
 */
export function describeSqPosition(position) {
  if (!position || typeof position !== 'object') return '';
  const low = Number(position.rep_low);
  const high = Number(position.rep_high);
  if (!Number.isInteger(low) || !Number.isInteger(high) || low < 1) return '';
  const band = low === high ? `${low} reps` : `${low}-${high} reps`;

  if (position.kind === 'kilo_cell' && position.kilo_label) {
    const model = position.kilo_model ? `, ${position.kilo_model}` : '';
    const scheme = position.scheme ? `: ${position.scheme}` : '';
    return `KILO ${position.kilo_label}${model}${scheme} (${band} on the A-series)`;
  }
  if (position.kind === 'rep_band') {
    const source = position.source ? ` (${position.source} rep band)` : '';
    return `${band} on the A-series${source}`;
  }
  return '';
}

const REFUSAL_MESSAGES = {
  coach_plan_exhausted: 'Coach plan exhausted \u2014 extend the plan, or write this block yourself.',
  no_coach_plan: 'This athlete has no coach plan \u2014 choose another sequence, or write this block yourself.',
  kilo_model_complete: 'KILO long-term model complete \u2014 every block of the model has been trained. Choose the next block yourself.',
  no_observed_transition: 'The corpus has never seen a block follow this athlete\u2019s last one \u2014 choose the next block yourself.',
  transition_matrix_unavailable: 'Corpus matrix not available on the server.',
  transition_matrix_invalid: 'Corpus matrix not available on the server \u2014 the file it has is not readable.',
};

/**
 * The message for a sequence policy refusing, or '' when the error is not one.
 *
 * Shown as it is. Nothing asks another policy instead: a block from a sequence
 * the coach did not choose, under the name of the one they did, is worse than
 * no suggestion.
 */
export function sequenceRefusalMessage(error) {
  const data = error?.response?.data;
  if (!data || data.refusal !== true) return '';
  return REFUSAL_MESSAGES[data.code]
    || data.error
    || 'The chosen sequence cannot recommend a next block. Choose it yourself.';
}

/**
 * Change one field of the brief.
 *
 * Changing the phase or the strength quality drops the position: a KILO cell
 * belongs to one phase and quality, and the service refuses a brief whose
 * position does not match it. The policy stays, because it still chose the
 * block the coach is adjusting.
 */
export function editBriefField(brief, field, value) {
  const next = { ...brief, [field]: value };
  if ((field === 'phase' || field === 'sq') && value !== brief?.[field]) {
    delete next.sqPosition;
  }
  return next;
}
